import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, EyeOff, Eye, Trash2, X } from "lucide-react";
import api from "@/lib/api";

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/categories", { params: { include_unpublished: true } });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (payload, id) => {
    try {
      if (id) {
        await api.patch(`/admin/categories/${id}`, payload);
        toast.success("Category updated");
      } else {
        await api.post("/admin/categories", payload);
        toast.success("Category created");
      }
      setEditing(null);
      setCreating(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const togglePublished = async (c) => {
    await save({ published: !c.published }, c.id);
  };

  const deactivate = async (c) => {
    if (!window.confirm(`Deactivate "${c.name}"? Existing dish references remain intact.`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Deactivated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex items-end justify-between">
        <div>
          <span className="kbd-eyebrow">Admin</span>
          <h1 className="mt-3 font-serif-display text-3xl font-semibold text-secondary">Categories</h1>
        </div>
        <button
          data-testid="admin-cat-add-button"
          onClick={() => setCreating(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4" /> Add category
        </button>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-border/70 bg-card" data-testid="admin-categories-table">
        <table className="w-full text-sm">
          <thead className="border-b border-border/70 bg-muted/50 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            <tr>
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">Published</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3">Extras (per head)</th>
              <th className="px-4 py-3">Rate</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-muted-foreground" data-testid="admin-cat-empty">
                  No categories yet.
                </td>
              </tr>
            )}
            {items.map((c, i) => (
              <tr key={c.id} className="border-b border-border/60 last:border-none" data-testid={`admin-cat-row-${c.id}`}>
                <td className="px-4 py-3 text-muted-foreground">{i + 1}</td>
                <td className="px-4 py-3 font-medium text-secondary">{c.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{c.slug}</td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`admin-cat-toggle-published-${c.id}`}
                    onClick={() => togglePublished(c)}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
                      c.published ? "bg-accent/20 text-secondary" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {c.published ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    {c.published ? "Published" : "Unpublished"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <span className={c.active ? "text-secondary" : "text-muted-foreground"}>
                    {c.active ? "Yes" : "No"}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{c.is_extra ? "Yes" : "—"}</td>
                <td className="px-4 py-3" data-testid={`admin-cat-rate-${c.id}`}>
                  {c.is_extra
                    ? (c.extra_rate_per_head === null || c.extra_rate_per_head === undefined
                        ? <span className="text-primary font-semibold">TO BE CONFIGURED</span>
                        : `₹${Number(c.extra_rate_per_head).toLocaleString("en-IN")} / head`)
                    : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button
                      data-testid={`admin-cat-edit-${c.id}`}
                      onClick={() => setEditing(c)}
                      className="grid h-8 w-8 place-items-center rounded-full border border-border/70 hover:bg-muted"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`admin-cat-delete-${c.id}`}
                      onClick={() => deactivate(c)}
                      className="grid h-8 w-8 place-items-center rounded-full border border-border/70 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(editing || creating) && (
        <CategoryModal
          initial={editing}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSave={(payload) => save(payload, editing?.id)}
        />
      )}
    </div>
  );
}

function CategoryModal({ initial, onClose, onSave }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [displayOrder, setDisplayOrder] = useState(initial?.display_order ?? 0);
  const [published, setPublished] = useState(initial?.published ?? true);
  const [active, setActive] = useState(initial?.active ?? true);
  const [isExtra, setIsExtra] = useState(initial?.is_extra ?? false);
  const [extraRate, setExtraRate] = useState(
    initial?.extra_rate_per_head === null || initial?.extra_rate_per_head === undefined
      ? "" : String(initial.extra_rate_per_head)
  );

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name required");
    onSave({
      name: name.trim(),
      description: description.trim() || null,
      display_order: Number(displayOrder) || 0,
      published,
      active,
      is_extra: isExtra,
      extra_rate_per_head: isExtra
        ? (extraRate.trim() === "" ? null : Number(extraRate))
        : null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="admin-cat-modal">
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-2xl border border-border/70 bg-card p-6"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-xl font-semibold text-secondary">
            {initial ? "Edit category" : "New category"}
          </h3>
          <button type="button" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-5 space-y-4">
          <label className="block text-sm">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Name</span>
            <input data-testid="admin-cat-name-input" required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5" />
          </label>
          <label className="block text-sm">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Description (optional)</span>
            <textarea data-testid="admin-cat-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block text-sm">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Display order</span>
              <input type="number" data-testid="admin-cat-order-input" value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)} className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5" />
            </label>
            <label className="flex items-center gap-2 pt-6 text-sm">
              <input type="checkbox" data-testid="admin-cat-extra-toggle" checked={isExtra} onChange={(e) => setIsExtra(e.target.checked)} />
              Extras (per head)
            </label>
            {isExtra && (
              <label className="block text-sm sm:col-span-2">
                <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Per-head rate (₹) — leave empty for "TO BE CONFIGURED"
                </span>
                <input type="number" min="0" step="0.01" data-testid="admin-cat-extra-rate-input"
                       value={extraRate} onChange={(e) => setExtraRate(e.target.value)}
                       className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5" />
              </label>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="admin-cat-published-toggle" checked={published} onChange={(e) => setPublished(e.target.checked)} />
              Published
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="admin-cat-active-toggle" checked={active} onChange={(e) => setActive(e.target.checked)} />
              Active
            </label>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="submit" data-testid="admin-cat-save-button" className="btn-primary">Save</button>
        </div>
      </form>
    </div>
  );
}
